import { motion } from 'framer-motion';

/**
 * Reusable infinite horizontal marquee of logos / brand cards.
 * - `items` : array of { name, icon?, dot?, tagline? }
 *     • name   : label shown on the card
 *     • icon   : optional white-tinted SVG URL (e.g. Simple Icons CDN)
 *     • dot    : optional accent color for a small circle mark
 *     • tagline: optional small text under the name
 * - `heading` : uppercase eyebrow label above the strip
 * - `subheading` : title line (supports plain text)
 * - `direction` : "left" (default) or "right"
 * - `speedSec`  : marquee cycle in seconds (default 40)
 */
export const LogoMarquee = ({
  items = [],
  heading = '',
  subheading = '',
  direction = 'left',
  speedSec = 40,
  'data-testid': testId,
}) => {
  const loop = [...items, ...items]; // duplicated for seamless scroll
  const animClass = direction === 'right' ? 'animate-marquee-reverse' : 'animate-marquee';

  return (
    <section
      className="relative py-16 lg:py-20 overflow-hidden z-10"
      data-testid={testId}
    >
      {/* soft glow accents */}
      <div className="absolute top-0 left-1/4 w-96 h-32 bg-[#31638C] opacity-[0.08] rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-32 bg-[#4A8BC2] opacity-[0.06] rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#31638C]/25 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#31638C]/25 to-transparent" />

      {(heading || subheading) && (
        <motion.div
          className="text-center mb-10 lg:mb-12 px-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {heading && (
            <span className="text-xs uppercase tracking-[0.28em] text-[#31638C] font-semibold">
              {heading}
            </span>
          )}
          {subheading && (
            <h3 className="text-2xl lg:text-3xl font-bold tracking-tight mt-3 text-white">
              {subheading}
            </h3>
          )}
        </motion.div>
      )}

      <div className="relative">
        <div
          className={`flex gap-5 ${animClass} whitespace-nowrap`}
          style={{ animationDuration: `${speedSec}s` }}
        >
          {loop.map((it, i) => (
            <div
              key={`${it.name}-${i}`}
              className="flex items-center gap-3 flex-shrink-0 px-6 py-4 rounded-2xl bg-[#131E2C]/80 backdrop-blur-sm border border-[#5D676E]/25 hover:border-[#31638C]/60 transition-all duration-500 group hover:-translate-y-1 hover:shadow-lg hover:shadow-[#31638C]/20"
              data-testid={`logo-chip-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {it.icon ? (
                <img
                  src={it.icon}
                  alt={it.name}
                  className="h-6 w-auto opacity-80 group-hover:opacity-100 transition-opacity"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0 shadow-[0_0_10px_currentColor]"
                  style={{ backgroundColor: it.dot || '#4A8BC2', color: it.dot || '#4A8BC2' }}
                />
              )}
              <div className="flex flex-col leading-tight">
                <span className="text-base lg:text-lg font-semibold text-white tracking-tight">
                  {it.name}
                </span>
                {it.tagline && (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mt-0.5">
                    {it.tagline}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        {/* fade edges */}
        <div className="absolute top-0 left-0 h-full w-24 lg:w-40 bg-gradient-to-r from-[#0A1420] to-transparent pointer-events-none z-10" />
        <div className="absolute top-0 right-0 h-full w-24 lg:w-40 bg-gradient-to-l from-[#0A1420] to-transparent pointer-events-none z-10" />
      </div>
    </section>
  );
};
